// AST Graph Builder — public barrel and orchestration entry point
// (ARCHITECTURE.md §6.7, §5.3).
//
// `buildAstGraph` ties the pipeline together: scan → (cache check) → analyze →
// merge → (cache write). It is the only place in the AST layer that touches the
// store. Incremental rebuilds re-analyze only files whose content hash changed
// since the last write; unchanged files are restored from the cached IR.
//
// AST edges reference unresolved import specifiers (e.g. "./foo", "react"), not
// file ids, so they cannot be stored in the row-level `edges` table — that
// resolution happens in the Dependency Graph Builder (Phase 5). The full
// `FileAnalysisResult` IR is therefore cached as a JSON blob in the metadata
// table, keyed by relative path, alongside the file row that tracks its hash.

import { readFile } from "node:fs/promises";

import type { AnalyzerRegistry } from "../analyzer/registry.js";
import type { FileAnalysisResult } from "../analyzer/types.js";
import { FileAnalysisResultSchema } from "../analyzer/types.js";
import { applyProjectResolution } from "../analyzer/project-analyzer.js";
import { hashContent } from "../store/hasher.js";
import type { StoreHandle } from "../store/types.js";
import { getFileByPath } from "../store/reader.js";
import { upsertFile, upsertMetadata } from "../store/writer.js";
import { buildAstGraphFromResults } from "./builder.js";
import { scanFiles } from "./file-scanner.js";
import { resolveAllEdges } from "./specifier-resolver.js";
import type { AstGraph } from "./types.js";

export * from "./types.js";
export * from "./builder.js";
export * from "./file-scanner.js";
export * from "./specifier-resolver.js";

/** Metadata key under which a file's cached IR blob is stored. */
function cacheKey(relativePath: string): string {
  return `ast_result:${relativePath}`;
}

export interface BuildAstGraphResult {
  graph: AstGraph;
  /** File contents read during AST build (relative path → source). */
  fileContents: Map<string, string>;
}

export interface BuildAstGraphOptions {
  /** Project root; patterns and graph node ids are relative to it. */
  projectRoot: string;
  /** artifactId → glob patterns (the DSL `path_patterns`). */
  artifactPatterns: Map<string, string[]>;
  /** Analyzer registry resolving files to LanguageAnalyzers by extension. */
  registry: AnalyzerRegistry;
  /** Optional persistent store enabling incremental rebuilds. */
  store?: StoreHandle;
  /** Exclude patterns; defaults to the scanner's DEFAULT_GLOBAL_EXCLUDE. */
  globalExclude?: string[];
}

/**
 * Load a previously cached IR for `relativePath` from the store, validating it
 * at the trust boundary. Returns undefined if absent or no longer schema-valid
 * (forcing a re-analysis).
 */
function loadCachedResult(
  store: StoreHandle,
  relativePath: string,
): FileAnalysisResult | undefined {
  const raw = store.backend.getMetadata(cacheKey(relativePath));
  if (raw === undefined) return undefined;
  try {
    const parsed = FileAnalysisResultSchema.safeParse(JSON.parse(raw));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}

/** Persist a freshly analyzed file: its hash (file row) and its IR (blob). */
function writeResult(
  store: StoreHandle,
  relativePath: string,
  hash: string,
  analyzerId: string,
  result: FileAnalysisResult,
): void {
  upsertFile(
    store,
    relativePath,
    hash,
    result.language,
    result.metadata.lineCount,
    analyzerId,
  );
  upsertMetadata(store, cacheKey(relativePath), JSON.stringify(result));
}

/**
 * Build the AST Graph for a project (ARCHITECTURE.md §6.7).
 *
 * Steps: scan files → for each, hash and consult the store (hash match skips
 * analysis, restoring the cached IR) → analyze the rest via the registry
 * (files with no matching analyzer are skipped with a diagnostic) → merge into
 * an AstGraph → write new/changed IRs back to the store.
 */
export async function buildAstGraph(
  options: BuildAstGraphOptions,
): Promise<BuildAstGraphResult> {
  const { projectRoot, artifactPatterns, registry, store, globalExclude } =
    options;

  const { entries } = await scanFiles({
    projectRoot,
    artifactPatterns,
    globalExclude,
  });

  const results: FileAnalysisResult[] = [];
  const fileHashes = new Map<string, string>();
  const fileContents = new Map<string, string>();
  const changedFiles = new Set<string>();

  for (const entry of entries) {
    const analyzer = registry.getForFile(entry.relativePath);
    if (!analyzer) {
      // No analyzer for this extension: skip with a diagnostic (§6.6).
      console.warn(
        `[agent-contracts-analyzer] no analyzer for '${entry.relativePath}'; skipping.`,
      );
      continue;
    }

    const content = await readFile(entry.path, "utf8");
    fileContents.set(entry.relativePath, content);
    const hash = hashContent(content);
    fileHashes.set(entry.relativePath, hash);

    // Incremental: reuse the cached IR when the content hash is unchanged.
    if (store) {
      const existing = getFileByPath(store, entry.relativePath);
      if (existing && existing.hash === hash) {
        const cached = loadCachedResult(store, entry.relativePath);
        if (cached) {
          results.push(cached);
          continue;
        }
      }
    }

    changedFiles.add(entry.relativePath);
    const result = await analyzer.analyze(entry.relativePath, content);
    results.push(result);

    if (store) {
      writeResult(store, entry.relativePath, hash, analyzer.language, result);
    }
  }

  // Cross-file symbol resolution (proposal §3.5): ProjectAnalyzers (e.g. the
  // TypeScript Program API) rewrite bare-name `calls` / `type_reference` edges
  // to full cross-file symbolIds. Skipped on a full cache hit — cached IRs
  // already contain post-resolution edges from the previous run.
  let symbolResolvedResults = results;
  if (changedFiles.size > 0) {
    const projectAnalyzers = registry.listProjectAnalyzers();
    if (projectAnalyzers.length > 0) {
      const fileMap = new Map(
        symbolResolvedResults.map((result) => [result.filePath, result]),
      );
      for (const analyzer of projectAnalyzers) {
        const resolution = await analyzer.analyzeProject({
          projectRoot,
          files: fileMap,
          changedFiles,
        });
        symbolResolvedResults = applyProjectResolution(
          symbolResolvedResults,
          resolution,
        );
        for (const result of symbolResolvedResults) {
          fileMap.set(result.filePath, result);
        }
      }
    }
  }

  // Resolve import/re-export specifiers to project-relative file paths so edge
  // `to` values match graph node ids (enabling downstream BFS traversal). All
  // scanned files — including those with no analyzer — are valid resolution
  // targets (e.g. a .ts file importing a .yaml schema). Done here, after
  // analysis/caching but before the merge, so cached IRs are resolved too.
  const projectFiles = new Set(entries.map((entry) => entry.relativePath));
  const resolvedResults = symbolResolvedResults.map((result) => ({
    ...result,
    edges: resolveAllEdges({ edges: result.edges, projectFiles }),
  }));

  // Re-cache with post-resolution IR so the next full cache hit skips
  // ts.createProgram and already has resolved symbol/import edges.
  // Only write files that actually differ from the cached version:
  //   - changed files (content changed) are always written
  //   - cached files are written only when resolution produced different edges
  if (store && changedFiles.size > 0) {
    for (const result of resolvedResults) {
      const hash = fileHashes.get(result.filePath);
      if (!hash) continue;
      if (changedFiles.has(result.filePath)) {
        writeResult(store, result.filePath, hash, result.language, result);
      } else {
        const newJson = JSON.stringify(result);
        const cachedJson = store.backend.getMetadata(cacheKey(result.filePath));
        if (newJson !== cachedJson) {
          writeResult(store, result.filePath, hash, result.language, result);
        }
      }
    }
  }

  return {
    graph: buildAstGraphFromResults(resolvedResults, fileHashes),
    fileContents,
  };
}
